import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchAllOrders,
  updateOrderStatus,
  setSelectedOrder,
  clearSelectedOrder,
} from "../../../redux/slices/adminOrderSlice";

// ─────────────────────────────────────────────────────────────────────────────
// Couriers Qikink typically uses — extend this list as needed
// ─────────────────────────────────────────────────────────────────────────────
const COURIER_OPTIONS = [
  "Bluedart Express",
  "Delhivery AIR",
  "XpressBees",
  "Ecom Express",
  "DTDC",
  "India Post",
  "Other",
];

const getStatusColor = (status) => {
  switch (status) {
    case 'Processing': return 'bg-yellow-100 text-yellow-800';
    case 'Shipped':    return 'bg-blue-100 text-blue-800';
    case 'Delivered':  return 'bg-green-100 text-green-800';
    case 'Cancelled':  return 'bg-red-100 text-red-800';
    default:           return 'bg-gray-100 text-gray-800';
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Order Details Modal (view only, no changes)
// ─────────────────────────────────────────────────────────────────────────────
const OrderDetailsModal = ({ order, onClose }) => {
  if (!order) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Order Details</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-3">Order Information</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Order ID:</span> #{order._id}</p>
                <p><span className="font-medium">Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </p>
                <p><span className="font-medium">Total Price:</span> ₹{order.totalPrice?.toFixed(2)}</p>
                <p><span className="font-medium">Payment Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                    order.isPaid || order.paymentStatus === 'paid' || order.paymentStatus === 'completed'
                      ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {order.paymentStatus || (order.isPaid ? 'Paid' : 'Unpaid')}
                  </span>
                </p>
                <p><span className="font-medium">Payment Method:</span> {order.paymentMethod}</p>
                <p><span className="font-medium">Created At:</span> {new Date(order.createdAt).toLocaleString()}</p>
                {order.shippedAt && (
                  <p><span className="font-medium">Shipped At:</span> {new Date(order.shippedAt).toLocaleString()}</p>
                )}
                {order.deliveredAt && (
                  <p><span className="font-medium">Delivered At:</span> {new Date(order.deliveredAt).toLocaleString()}</p>
                )}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-3">Shipped To</h3>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium">Name:</span>{' '}
                  {order.shippingAddress?.firstName
                    ? [order.shippingAddress.firstName, order.shippingAddress.lastName].filter(Boolean).join(' ')
                    : order.user?.name || 'N/A'}
                </p>
                <p>
                  <span className="font-medium">Mobile:</span>{' '}
                  {order.shippingAddress?.phone || order.shippingAddress?.mobile || 'N/A'}
                </p>
              </div>
              <h3 className="font-semibold mt-4 mb-2 pt-3 border-t border-gray-200">Billed By</h3>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium">Name:</span>{' '}
                  {order.user?.name || 'N/A'}
                </p>
                <p>
                  <span className="font-medium">Email:</span>{' '}
                  {order.user?.email || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Tracking Info — now shows AWB + courier instead of a link */}
          {order.trackingNumber && (
            <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
              <h3 className="font-semibold mb-2 text-blue-900">Tracking Information</h3>
              <p className="text-sm mb-1">
                <span className="font-medium">AWB Number:</span> {order.trackingNumber}
              </p>
              <p className="text-sm">
                <span className="font-medium">Courier:</span> {order.courierName || 'N/A'}
              </p>
            </div>
          )}

          {order.cancellationReason && (
            <div className="bg-red-50 p-4 rounded-lg mb-6 border border-red-200">
              <h3 className="font-semibold mb-2 text-red-900">Cancellation Reason</h3>
              <p className="text-sm text-red-800">{order.cancellationReason}</p>
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold mb-3">Shipping Address</h3>
            <div className="text-sm space-y-1">
              {(order.shippingAddress?.firstName || order.shippingAddress?.lastName) && (
                <p className="font-medium">
                  {[order.shippingAddress.firstName, order.shippingAddress.lastName].filter(Boolean).join(' ')}
                </p>
              )}
              <p>{order.shippingAddress?.address}</p>
              <p>{order.shippingAddress?.city}, {order.shippingAddress?.state}</p>
              <p>{order.shippingAddress?.postalCode}, {order.shippingAddress?.country}</p>
              {(order.shippingAddress?.phone || order.shippingAddress?.mobile) && (
                <p>Contact: {order.shippingAddress.phone || order.shippingAddress.mobile}</p>
              )}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">Order Items ({order.orderItems?.length || 0})</h3>
            <div className="space-y-3">
              {order.orderItems?.map((item, index) => (
                <div key={index} className="flex items-center space-x-4 bg-white p-3 rounded border">
                  <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                  <div className="flex-1">
                    <h4 className="font-medium">{item.name}</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      {item.size && <p>Size: {item.size}</p>}
                      {item.color && <p>Color: {item.color}</p>}
                      <p>Qty: {item.quantity}</p>
                      {item.freeQuantity > 0 && (
                        <p className="font-semibold text-green-700">Free (offer): {item.freeQuantity}</p>
                      )}
                      <p>Unit Price: ₹{item.price?.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-lg">₹{(item.price * item.quantity)?.toFixed(2)}</p>
                  </div>
                </div>
              ))}
              <div className="border-t pt-3 mt-4">
                {order.discountTotal > 0 && (
                  <div className="text-sm text-gray-700 px-3 pb-2 space-y-1">
                    <div className="flex justify-between"><span>Subtotal</span><span>₹{order.subtotal?.toFixed(2)}</span></div>
                    <div className="flex justify-between text-green-700">
                      <span>{order.offer?.name || 'Offer'}</span><span>− ₹{order.discountTotal?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center bg-white p-3 rounded font-semibold text-lg">
                  <span>Order Total:</span>
                  <span>₹{order.totalPrice?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const OrderManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);
  const { orders, selectedOrder, loading, error } = useSelector((state) => state.adminOrders);

  const [editingOrder,       setEditingOrder]       = useState(null);
  const [selectedStatus,     setSelectedStatus]     = useState('');

  // Shipped fields — CHANGED: awbNumber + courierName instead of trackingLink URL
  const [awbNumber,          setAwbNumber]          = useState('');
  const [courierName,        setCourierName]        = useState('');

  // Cancelled field
  const [cancellationReason, setCancellationReason] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    dispatch(fetchAllOrders());
  }, [dispatch, user, navigate]);

  const handleOrderClick = (order) => {
    dispatch(setSelectedOrder(order));
  };

  const handleCloseModal = () => {
    dispatch(clearSelectedOrder());
  };

  const handleStatusChange = (orderId, newStatus) => {
    setEditingOrder(orderId);
    setSelectedStatus(newStatus);
    // Reset inline form fields
    setAwbNumber('');
    setCourierName('');
    setCancellationReason('');
  };

  const handleCancelEdit = () => {
    setEditingOrder(null);
    setSelectedStatus('');
    setAwbNumber('');
    setCourierName('');
    setCancellationReason('');
  };

  const handleSubmitStatusChange = async (orderId) => {
    // Validate Shipped fields
    if (selectedStatus === 'Shipped') {
      if (!awbNumber.trim()) {
        alert('Please enter the AWB / Tracking number');
        return;
      }
      if (!courierName) {
        alert('Please select the courier');
        return;
      }
    }

    // Validate Cancelled field
    if (selectedStatus === 'Cancelled' && !cancellationReason.trim()) {
      alert('Please enter a cancellation reason');
      return;
    }

    const payload = { id: orderId, status: selectedStatus };
    if (selectedStatus === 'Shipped') {
      payload.awbNumber  = awbNumber.trim();
      payload.courierName = courierName;
    }
    if (selectedStatus === 'Cancelled') {
      payload.cancellationReason = cancellationReason.trim();
    }

    await dispatch(updateOrderStatus(payload));
    handleCancelEdit();
    dispatch(fetchAllOrders());
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg">Loading orders...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Order Management</h1>

      {orders && orders.length > 0 ? (
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <React.Fragment key={order._id}>
                  <tr className="hover:bg-gray-50">
                    <td
                      className="px-6 py-4 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      onClick={() => handleOrderClick(order)}
                    >
                      #{order._id.slice(-8)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {order.shippingAddress?.firstName
                        ? [order.shippingAddress.firstName, order.shippingAddress.lastName].filter(Boolean).join(' ')
                        : order.user?.name || 'N/A'}
                      <div className="text-xs text-gray-500">{order.user?.email || ''}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {order.shippingAddress?.phone || order.shippingAddress?.mobile || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold">₹{order.totalPrice?.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        order.isPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {order.isPaid ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {editingOrder === order._id ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSubmitStatusChange(order._id)}
                            className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                          >
                            ✓ Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="bg-gray-400 text-white px-3 py-1 rounded text-xs hover:bg-gray-500"
                          >
                            ✕ Cancel
                          </button>
                        </div>
                      ) : (
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusChange(order._id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Processing">Processing</option>
                          <option value="Shipped">Shipped</option>
                          <option value="Delivered">Delivered</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      )}
                    </td>
                  </tr>

                  {/* ── Inline Form Row ────────────────────────────────────────── */}
                  {editingOrder === order._id && (
                    <tr>
                      <td colSpan="8" className="px-6 py-4 bg-blue-50 border-t-2 border-blue-200">

                        {/* ── SHIPPED: AWB + Courier (replaces old URL input) ─────── */}
                        {selectedStatus === 'Shipped' && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-blue-900">📦 Shipping Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium mb-1">
                                  AWB / Tracking Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={awbNumber}
                                  onChange={(e) => setAwbNumber(e.target.value)}
                                  placeholder="e.g. 90422687121"
                                  className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">
                                  Courier <span className="text-red-500">*</span>
                                </label>
                                <select
                                  value={courierName}
                                  onChange={(e) => setCourierName(e.target.value)}
                                  className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                  required
                                >
                                  <option value="">— Select courier —</option>
                                  {COURIER_OPTIONS.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500">
                              The customer will be able to track their order live on your website using this AWB number.
                            </p>
                          </div>
                        )}

                        {/* ── CANCELLED ────────────────────────────────────────────── */}
                        {selectedStatus === 'Cancelled' && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-red-900">✕ Cancellation Details</h4>
                            <div>
                              <label className="block text-sm font-medium mb-1">
                                Reason for Cancellation <span className="text-red-500">*</span>
                              </label>
                              <textarea
                                value={cancellationReason}
                                onChange={(e) => setCancellationReason(e.target.value)}
                                placeholder="e.g. Customer requested cancellation, Out of stock..."
                                className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-red-500"
                                rows="3"
                                required
                              />
                            </div>
                          </div>
                        )}

                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 text-lg">No orders found.</div>
      )}

      {selectedOrder && (
        <OrderDetailsModal order={selectedOrder} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default OrderManagement;
